import {AxiosRequestConfig} from "axios";
import httpResource from "@/components/infrastructures/functions/http/httpResource";
import {AuthPolicy, legacyPolicy, parseAuthPolicy} from "@/components/domains/auth/authPolicy";

/** `client` key the backend maps to this front's landing URLs. */
export const CLIENT_KEY = "admin";

/** Never leave a screen waiting: after this, use today's (legacy) policy. */
const POLICY_TIMEOUT_MS = 4000;

/**
 * The new endpoints answer with their own messages (and 401/403 for some
 * reasons). Bypass the global interceptor so it neither logs the admin out
 * nor redirects to /login; each screen shows the message inline.
 */
interface QuietConfig extends AxiosRequestConfig {
    handlerEnabled: boolean;
}

function quiet(timeout?: number): QuietConfig {
    const config: QuietConfig = {handlerEnabled: false};
    if (timeout) config.timeout = timeout;
    return config;
}

let inflight: Promise<AuthPolicy> | null = null;

export default class PasswordlessService {

    /**
     * `GET /auth/policy`. `fresh` forces a new request (login screens: the
     * policy can flip at the cut-over instant without a deploy). Any failure
     * (404 while the flags are off, network, timeout) → legacy policy.
     */
    public static loadPolicy(opts: { fresh?: boolean } = {}): Promise<AuthPolicy> {
        if (inflight && !opts.fresh) return inflight;
        inflight = (async () => {
            try {
                const res = await Promise.race([
                    httpResource.get("/auth/policy", quiet(POLICY_TIMEOUT_MS)),
                    new Promise<null>((resolve) => setTimeout(() => resolve(null), POLICY_TIMEOUT_MS)),
                ]);
                if (res && res.status === 200) return parseAuthPolicy(res.data);
            } catch (e) {
                // keep legacy
            }
            return legacyPolicy();
        })();
        return inflight;
    }

    /** 202 whether or not the address is registered. */
    public static async requestMagicLink(email: string): Promise<void> {
        await httpResource.post("/auth/magic-link", {email, client: CLIENT_KEY}, quiet());
    }

    /** 200 + Set-Cookie (same as `/auth/login`). */
    public static async verifyMagicLink(token: string): Promise<void> {
        await httpResource.post("/auth/magic-link/verify", {token}, quiet());
    }

    /** Accounts without an email address. 200 + Set-Cookie. */
    public static async loginWithUsername(username: string, password: string): Promise<void> {
        await httpResource.post("/auth/login/username", {username, password}, quiet());
    }

    /** Signed in. 202 (also when the address is taken — its owner is told by mail). */
    public static async requestMailAddressSetup(email: string): Promise<void> {
        await httpResource.post("/mail-address/setup", {email, client: CLIENT_KEY}, quiet());
    }

    /** No sign-in needed. Returns the saved address. */
    public static async confirmMailAddress(token: string): Promise<string> {
        const res = await httpResource.post("/auth/mail-address/confirm", {token}, quiet());
        const data = res.data as { mailAddress?: unknown } | null;
        return data && typeof data.mailAddress === "string" ? data.mailAddress : "";
    }
}

/**
 * Remove the fragment (`#token=…`) from the address bar without a reload.
 * Keeps `history.state` so vue-router's own state key survives.
 */
export function clearUrlFragment(): void {
    if (!window.location.hash) return;
    try {
        history.replaceState(history.state, "", window.location.pathname + window.location.search);
    } catch (e) {
        // ignore — the token was already read
    }
}
